import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Menu, Bell, Search, Info, HelpCircle, CreditCard, Kanban, Users, LayoutDashboard, FolderKanban, Star, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

export default function Header({ setIsSidebarOpen }) {
  const [notifications, setNotifications] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    try {
      const resp = await fetch('/api/notifications', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setNotifications(data);
      }
    } catch (err) {
      console.error("Erro ao buscar notificações:", err);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // 1 min
    return () => clearInterval(interval);
  }, []);

  const hasUnread = notifications.some(n => !n.read);

  const clearAllNotifications = async (e) => {
    e.stopPropagation();
    try {
      const resp = await fetch('/api/notifications', {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (resp.ok) setNotifications([]);
    } catch (err) {
      console.error("Erro ao limpar notificações:", err);
    }
  };

  const markRead = async (id) => {
    try {
      await fetch(`/api/notifications/${id}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (err) {
      console.error("Erro ao marcar como lida:", err);
    }
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-slate-100/80">
      <div className="flex items-center justify-between px-4 lg:px-8 h-16">
        {/* Left Side: Mobile Menu & Breadcrumb/Title */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden h-9 w-9 text-slate-500 hover:text-slate-900"
            onClick={() => setIsSidebarOpen(true)}
            data-testid="open-sidebar-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          
          <div className="hidden lg:flex flex-col">
            <h1 className="text-sm font-bold text-slate-900 tracking-tight leading-none">Painel Principal</h1>
            <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mt-1">Gestão de Agência</p>
          </div>
        </div>

        {/* Right Side: Actions & Notifications */}
        <div className="flex items-center gap-1.5 md:gap-3">
          {/* Quick Search Button */}
          <button 
            onClick={() => setIsSearchOpen(true)}
            className="hidden sm:flex items-center gap-2 px-3 h-9 rounded-xl bg-[#F9F9F9] border border-slate-100/50 text-slate-500 hover:text-indigo-600 hover:border-indigo-100 transition-all group shadow-sm"
          >
            <Search className="h-4 w-4 group-hover:scale-110 transition-transform" />
            <span className="text-xs font-semibold pr-8">Buscar no sistema...</span>
            <kbd className="hidden md:inline-flex h-5 w-8 items-center justify-center rounded border bg-white px-1 font-mono text-[10px] font-bold text-slate-400 select-none">
              ⌘K
            </kbd>
          </button>

          <div className="h-6 w-[1px] bg-slate-100 mx-1 hidden sm:block" />

          {/* Help Button */}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-xl text-slate-500 hover:text-indigo-600 hover:bg-indigo-50"
            title="Centro de Ajuda"
          >
            <HelpCircle className="h-5 w-5" />
          </Button>

          {/* Notification Center / Alerts */}
          <div className="relative group">
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-9 w-9 rounded-xl transition-all duration-200",
                hasUnread 
                  ? "text-indigo-500 bg-indigo-50 hover:bg-indigo-100 hover:text-indigo-600 shadow-sm" 
                  : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
              )}
              title="Alertas e Notificações"
            >
              <Bell className={cn("h-5 w-5", hasUnread && "animate-pulse")} />
              {hasUnread && (
                <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-red-500 border-2 border-white" />
              )}
            </Button>
            
            {/* Notifications Popover */}
            <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-slate-100 p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 translate-y-2 group-hover:translate-y-0 z-50">
              <div className="p-3 border-b border-slate-50 mb-1 flex items-center justify-between">
                <h3 className="text-xs font-bold text-slate-900">Notificações Recentes</h3>
                <button 
                  onClick={clearAllNotifications}
                  className="text-[10px] text-indigo-500 hover:text-indigo-700 font-black uppercase tracking-widest p-1 hover:bg-indigo-50 rounded"
                >
                  Limpar Tudo
                </button>
              </div>
              
              {notifications.length > 0 ? (
                <div className="space-y-1 max-h-80 overflow-y-auto premium-scrollbar">
                  {notifications.map((notif) => (
                    <div 
                      key={notif.id}
                      onClick={() => {
                        markRead(notif.id);
                        if (notif.link) navigate(notif.link);
                      }}
                      className={cn(
                        "p-3 rounded-xl transition-all cursor-pointer group/item border border-transparent",
                        notif.read ? "hover:bg-slate-50" : "bg-indigo-50/30 hover:border-indigo-100"
                      )}
                    >
                      <div className="flex gap-3">
                        <div className={cn(
                          "h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0",
                          notif.type === 'success' ? "bg-emerald-100 text-emerald-600" :
                          notif.type === 'warning' ? "bg-amber-100 text-amber-600" :
                          notif.type === 'error' ? "bg-red-100 text-red-600" :
                          "bg-indigo-100 text-indigo-600"
                        )}>
                          {notif.type === 'success' ? <CreditCard className="h-4 w-4" /> : <Info className="h-4 w-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-slate-900 truncate">{notif.title}</p>
                          <p className="text-[10px] text-slate-500 mt-0.5 leading-tight line-clamp-2">{notif.message}</p>
                          <p className="text-[9px] text-slate-400 font-bold mt-1 uppercase">
                            {new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        {!notif.read && (
                          <div className="h-2 w-2 rounded-full bg-indigo-500 mt-1 shadow-sm" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-6 text-center">
                  <Bell className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                  <p className="text-xs font-medium text-slate-400">Nenhuma notificação por aqui</p>
                </div>
              )}
              
              <button className="w-full mt-2 p-2.5 text-[11px] font-bold text-slate-500 hover:text-slate-900 hover:bg-slate-50 rounded-xl transition-all">
                Ver todas as notificações
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search Command Palette Overlay */}
      <SearchCommand isOpen={isSearchOpen} setIsOpen={setIsSearchOpen} />
    </header>
  );
}

function SearchCommand({ isOpen, setIsOpen }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    const down = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen((open) => !open);
      }
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [setIsOpen, isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  if (!isOpen) return null;

  const quickLinks = [
    { label: 'Ir para Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { label: 'Gerenciar Leads', icon: Kanban, path: '/crm' },
    { label: 'Ver Clientes', icon: Users, path: '/clientes' },
    { label: 'Projetos Ativos', icon: FolderKanban, path: '/projetos' },
    { label: 'Faturas & Vendas', icon: CreditCard, path: '/financeiro' },
    { label: 'Meu Plano/Assinat.', icon: Star, path: '/assinaturas' },
    { label: 'Master Panel', icon: Crown, path: '/master' },
  ];

  const filteredLinks = query 
    ? quickLinks.filter(l => l.label.toLowerCase().includes(query.toLowerCase()))
    : quickLinks;

  const handleNavigate = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] bg-slate-900/40 backdrop-blur-sm flex items-start justify-center pt-20 p-4 animate-in fade-in duration-200"
      onClick={() => setIsOpen(false)}
      onKeyDown={(e) => { if (e.key === 'Escape') setIsOpen(false); }}
    >
      <div 
        className="w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden animate-in zoom-in duration-200"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center px-4 py-4 border-b border-slate-100 bg-white">
          <Search className="h-5 w-5 text-indigo-500" />
          <input 
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.stopPropagation();
                setIsOpen(false);
              }
            }}
            className="flex-1 ml-3 bg-transparent border-none outline-none text-slate-900 placeholder:text-slate-400 text-sm font-bold"
            placeholder="O que você deseja buscar hoje?"
          />
          <kbd className="px-1.5 py-0.5 text-[10px] font-bold text-slate-400 bg-slate-50 border border-slate-200 rounded">ESC</kbd>
        </div>
        
        <div className="max-h-[350px] overflow-y-auto p-2 premium-scrollbar">
          <div className="px-2 py-1.5">
            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
              {query ? 'Resultados da Busca' : 'Atalhos de Acesso Rápido'}
            </h4>
            <div className="space-y-1">
              {filteredLinks.length > 0 ? (
                filteredLinks.map((item, i) => (
                  <button 
                    key={i}
                    className="w-full flex items-center gap-3 p-3 rounded-xl text-sm font-bold text-slate-600 hover:bg-[#F9F9F9] hover:text-indigo-600 transition-all group border border-transparent hover:border-slate-100 hover:shadow-sm"
                    onClick={() => handleNavigate(item.path)}
                  >
                    <div className="h-8 w-8 rounded-lg bg-slate-50 flex items-center justify-center group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors">
                      <item.icon className="h-4 w-4" />
                    </div>
                    {item.label}
                  </button>
                ))
              ) : (
                <p className="text-xs text-slate-400 text-center py-4">Nenhum resultado encontrado para "{query}"</p>
              )}
            </div>
          </div>
        </div>

        <div className="p-3 bg-slate-50 border-t border-slate-100 flex justify-between items-center px-4">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">Kode Search Engine</p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
               <div className="h-5 w-5 bg-white border border-slate-200 rounded flex items-center justify-center text-[10px] font-black text-slate-400 shadow-sm">↵</div>
               <span className="text-[10px] font-bold text-slate-400 uppercase">Acessar</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}